import React, { useMemo } from 'react';
import FilterSelect from './FilterSelect';
import { Filter } from '@/hooks/search';
import { useClassificationOptions } from '@/features/alojamientos/hooks/useClassificationOptions';
import { useLocalities } from "@/features/alojamientos/hooks/useLocalities";
import { useStatusOptions } from '@/features/alojamientos/hooks/useStatusOptions';
import { useRegions } from '@/features/alojamientos/hooks/useRegions';
import { useSegments } from '@/features/alojamientos/hooks/useSegments';

// Definición de tipos mejorada
type FilterField = 'type' | 'category' | 'region' | 'locality' | 'status' | 'segment' | 'substatus';

interface OptionWithIdAndName {
  id: number | string;
  name: string;
}

type FilterValue = string | string[] | Record<string, boolean> | boolean;

interface FilterDropdownsProps {
  filters: Filter & {
    substatus?: string[];
  };
  onFilterChange: (filterName: keyof Filter, value: FilterValue) => void;
  handleMultiSelectChange: (field: FilterField, value: string) => void;
  removeMultiSelectValue: (field: FilterField, value: string) => void;
}

// Hook personalizado para manejar las opciones filtradas
const useFilteredOptions = (filters: FilterDropdownsProps['filters']) => {
  const { typeOptions, allCategoryOptions } = useClassificationOptions(null);
  const { localities } = useLocalities(null);
  const { statusOptions, allSubstatusOptions } = useStatusOptions(null);
  const { regions } = useRegions();
  const { segments } = useSegments();

  const filteredTypeOptions = useMemo(() => 
    filters.segment.length > 0
      ? typeOptions.filter(type => filters.segment.includes(type.segmentId))
      : typeOptions,
    [filters.segment, typeOptions]
  );

  // Filtrar categorías basándose en los tipos seleccionados
  const filteredCategoryOptions = useMemo(() => {
    if (filters.type.length === 0) {
      return [];
    }
    // Convertir los IDs de tipo de string a número para comparar
    const selectedTypeIds = filters.type.map(id => Number(id));
    return allCategoryOptions.filter(category => 
      selectedTypeIds.includes(category.typeId)
    );
  }, [filters.type, allCategoryOptions]);

  const filteredSubstatusOptions = useMemo(() => 
    filters.status.length > 0
      ? allSubstatusOptions.filter(substatus => 
          filters.status.includes(substatus.statusId.toString())
        )
      : allSubstatusOptions,
    [filters.status, allSubstatusOptions]
  );

  const filteredLocalities = useMemo(() => 
    filters.region.length > 0
      ? localities.filter(locality => 
          filters.region.includes(locality.department.region.id.toString())
        )
      : localities,
    [filters.region, localities]
  );

  return {
    filteredTypeOptions,
    filteredSubstatusOptions,
    filteredLocalities,
    categoryOptions: filteredCategoryOptions,
    allCategoryOptions,
    statusOptions,
    regions,
    segments,
    localities
  };
};

// Función helper para transformar opciones a formato de select
const transformOptions = (options: OptionWithIdAndName[]) => {
  return options.map((t) => ({
    value: t.id.toString(),
    label: t.name,
    id: t.id.toString()
  }));
};

// Hook personalizado para transformar opciones a formato de select
const useTransformedOptions = (options: OptionWithIdAndName[]) => {
  return useMemo(() => transformOptions(options), [options]);
};

const FilterDropdowns: React.FC<FilterDropdownsProps> = ({ 
  filters, 
  onFilterChange, 
  handleMultiSelectChange, 
  removeMultiSelectValue 
}) => {
  const {
    filteredTypeOptions,
    filteredSubstatusOptions,
    filteredLocalities,
    categoryOptions,
    allCategoryOptions,
    statusOptions,
    regions,
    segments,
    localities
  } = useFilteredOptions(filters);

  const typeOptionsL = useMemo(() => {
    const transformed = transformOptions(filteredTypeOptions);
    return [
      { value: 'none', label: 'Sin tipo asignado', id: 'none' },
      ...transformed
    ];
  }, [filteredTypeOptions]);

  const categoryOptionsL = useMemo(() => {
    const transformed = transformOptions(categoryOptions);
    return [
      { value: 'none', label: 'Sin categoría asignada', id: 'none' },
      ...transformed
    ];
  }, [categoryOptions]);

  const localitiesL = useTransformedOptions(filteredLocalities);
  const statusOptionsL = useTransformedOptions(statusOptions);
  const substatusOptionsL = useTransformedOptions(filteredSubstatusOptions);
  const regionsL = useTransformedOptions(regions);
  const segmentsL = useTransformedOptions(segments);

  // Handlers optimizados
  const handleRegionChange = (value: string) => {
    handleMultiSelectChange('region', value);
    const selectedRegionIds = [...filters.region, value];
    const validLocalities = localities.filter(locality => 
      selectedRegionIds.includes(locality.department.region.id.toString())
    );
    
    filters.locality.forEach(localityId => {
      if (!validLocalities.some(loc => loc.id.toString() === localityId)) {
        removeMultiSelectValue('locality', localityId);
      }
    });
  };

  const handleSegmentChange = (value: string) => {
    handleMultiSelectChange('segment', value);
    const selectedSegmentIds = [...filters.segment, value];
    const validTypes = filteredTypeOptions.filter(type => 
      selectedSegmentIds.includes(type.segmentId)
    );
    
    // Limpiar tipos que no corresponden a los segmentos seleccionados (excepto "none")
    filters.type.forEach(typeId => {
      if (typeId !== 'none' && !validTypes.some(type => type.id.toString() === typeId)) {
        removeMultiSelectValue('type', typeId);
      }
    });
    // Limpiar categorías que no corresponden a los tipos válidos (excepto "none")
    const validTypeIds = validTypes.map(type => type.id);
    const validCategories = validTypeIds.length > 0
      ? allCategoryOptions.filter(category => 
          validTypeIds.includes(category.typeId)
        )
      : [];
    filters.category.forEach(categoryId => {
      if (categoryId !== 'none' && !validCategories.some(category => category.id.toString() === categoryId)) {
        removeMultiSelectValue('category', categoryId);
      }
    });
  };

  const handleStatusChange = (value: string) => {
    handleMultiSelectChange('status', value);
    const selectedStatusIds = [...filters.status, value];
    const validSubstatuses = filteredSubstatusOptions.filter(substatus => 
      selectedStatusIds.includes(substatus.statusId.toString())
    );
    
    if (filters.substatus) {
      filters.substatus.forEach(substatusId => {
        if (!validSubstatuses.some(substatus => substatus.id.toString() === substatusId)) {
          removeMultiSelectValue('substatus', substatusId);
        }
      });
    }
  };

  const handleTypeChange = (value: string) => {
    handleMultiSelectChange('type', value);
    // Si se selecciona "none", no filtrar categorías
    if (value === 'none') {
      return;
    }
    const selectedTypeIds = [...filters.type, value]
      .filter(id => id !== 'none')
      .map(id => Number(id));
    const validCategories = selectedTypeIds.length > 0
      ? allCategoryOptions.filter(category => 
          selectedTypeIds.includes(category.typeId)
        )
      : [];
    
    // Limpiar categorías que no corresponden a los tipos seleccionados (excepto "none")
    filters.category.forEach(categoryId => {
      if (categoryId !== 'none' && !validCategories.some(category => category.id.toString() === categoryId)) {
        removeMultiSelectValue('category', categoryId);
      }
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
      <FilterSelect
        label="Segmento"
        placeholder="Seleccionar segmento"
        selectedValues={filters.segment}
        options={segmentsL}
        onSelectChange={handleSegmentChange}
        onRemoveValue={(value) => {
          removeMultiSelectValue('segment', value);
          const remainingSegmentIds = filters.segment.filter(id => id !== value);
          const validTypes = filteredTypeOptions.filter(type => 
            remainingSegmentIds.includes(type.segmentId)
          );
          // Limpiar tipos que no corresponden a los segmentos restantes (excepto "none")
          filters.type.forEach(typeId => {
            if (typeId !== 'none' && !validTypes.some(type => type.id.toString() === typeId)) {
              removeMultiSelectValue('type', typeId);
            }
          });
          // Limpiar categorías que no corresponden a los tipos restantes (excepto "none")
          const remainingTypeIds = validTypes.map(type => type.id);
          const validCategories = remainingTypeIds.length > 0
            ? allCategoryOptions.filter(category => 
                remainingTypeIds.includes(category.typeId)
              )
            : [];
          filters.category.forEach(categoryId => {
            if (categoryId !== 'none' && !validCategories.some(category => category.id.toString() === categoryId)) {
              removeMultiSelectValue('category', categoryId);
            }
          });
        }}
      />
      {/* Type/Class Filter */}
      <FilterSelect
        label="Tipo/Clase"
        placeholder="Seleccionar tipo"
        selectedValues={filters.type}
        options={typeOptionsL}
        onSelectChange={handleTypeChange}
        onRemoveValue={(value) => {
          removeMultiSelectValue('type', value);
          // Si se elimina "none", no hacer nada más
          if (value === 'none') {
            return;
          }
          // Limpiar categorías que no corresponden a los tipos restantes
          const remainingTypeIds = filters.type
            .filter(id => id !== value && id !== 'none')
            .map(id => Number(id));
          const validCategories = remainingTypeIds.length > 0
            ? allCategoryOptions.filter(category => 
                remainingTypeIds.includes(category.typeId)
              )
            : [];
          filters.category.forEach(categoryId => {
            if (categoryId !== 'none' && !validCategories.some(category => category.id.toString() === categoryId)) {
              removeMultiSelectValue('category', categoryId);
            }
          });
        }}
      />

      {/* Category Filter */}
      <FilterSelect
        label="Categoría"
        placeholder="Seleccionar categoría"
        selectedValues={filters.category}
        options={categoryOptionsL}
        onSelectChange={(value) => handleMultiSelectChange('category', value)}
        onRemoveValue={(value) => removeMultiSelectValue('category', value)}
      />

      {/* Status Filter */}
      <FilterSelect
        label="Estado"
        placeholder="Seleccionar estado"
        selectedValues={filters.status}
        options={statusOptionsL}
        onSelectChange={handleStatusChange}
        onRemoveValue={(value) => {
          removeMultiSelectValue('status', value);
          // Filtramos los subestados que no corresponden a los estados restantes
          const remainingStatusIds = filters.status.filter(id => id !== value);
          const validSubstatuses = filteredSubstatusOptions.filter(substatus => 
            remainingStatusIds.includes(substatus.statusId.toString())
          );
          if (filters.substatus) {
            filters.substatus.forEach(substatusId => {
              if (!validSubstatuses.some(substatus => substatus.id.toString() === substatusId)) {
                removeMultiSelectValue('substatus', substatusId);
              }
            });
          }
        }}
      />

      {/* Substatus Filter */}
      <FilterSelect
        label="Subestado"
        placeholder={!filters.status.length ? "Seleccione primero un estado" : "Seleccionar subestado"}
        selectedValues={filters.substatus || []}
        options={substatusOptionsL}
        onSelectChange={(value) => handleMultiSelectChange('substatus', value)}
        onRemoveValue={(value) => removeMultiSelectValue('substatus', value)}
        disabled={!filteredSubstatusOptions.length}
      />

      {/* Region Filter */}
      <FilterSelect
        label="Región"
        placeholder="Seleccionar región"
        selectedValues={filters.region}
        options={regionsL}
        onSelectChange={handleRegionChange}
        onRemoveValue={(value) => {
          removeMultiSelectValue('region', value);
          // También removemos las localidades que no corresponden a las regiones restantes
          const remainingRegionIds = filters.region.filter(id => id !== value);
          const validLocalities = localities.filter(locality => 
            remainingRegionIds.includes(locality.department.region.id.toString())
          );
          filters.locality.forEach(localityId => {
            if (!validLocalities.some(loc => loc.id.toString() === localityId)) {
              removeMultiSelectValue('locality', localityId);
            }
          });
        }}
      />

      {/* Locality Filter */}
      <FilterSelect
        label="Localidad"
        placeholder="Seleccionar localidad"
        selectedValues={filters.locality}
        options={localitiesL}
        onSelectChange={(value) => handleMultiSelectChange('locality', value)}
        onRemoveValue={(value) => removeMultiSelectValue('locality', value)}
      />
    </div>
  );
};

export default React.memo(FilterDropdowns);
